import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
	CircleCheckmarkIcon,
	CloseCircleIcon,
	Toast,
	type ToastAppearance,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import { useStreamingTranscriptionContext } from './StreamingTranscriptionContext';
import type { StreamingState, TranscriptEvent } from './useStreamingTranscription';

const UPDATE_INTERVAL_MS = 250;
const STICK_TO_BOTTOM_THRESHOLD_PX = 8;

/**
 * The toast measures its height once after it enters, so the log and the
 * partial line keep a fixed height while entries arrive.
 */
const Log = styled.div`
	height: 160px;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.inline}px;
	width: 100%;
`;

const Entry = styled.div`
	display: flex;
	align-items: flex-start;
	gap: ${tokens.gap.element}px;
	color: ${tokens.text.primary};
`;

const EntryIcon = styled.div<{ $color: string }>`
	flex-shrink: 0;
	margin-top: 2px;
	color: ${(props) => props.$color};
`;

const Partial = styled.div`
	min-height: calc(2em * ${tokens.typography.lineHeights.small});
	color: ${tokens.text.secondary};
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
`;

const Empty = styled.div`
	color: ${tokens.text.tertiary};
`;

/** Faint hint in the partial line while nobody is speaking. */
const Silence = styled.span`
	color: ${tokens.text.tertiary};
	opacity: 0.6;
	font-style: italic;
`;

/** Marks a new session; speaker numbers start over after it. */
const Break = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.gap.element}px;
	color: ${tokens.text.tertiary};
	font-size: ${tokens.typography.sizes.small}px;

	&::before,
	&::after {
		content: '';
		flex: 1;
		border-top: 1px solid ${tokens.border.default};
	}
`;

const Warning = styled.div`
	color: ${tokens.warning.text};
`;

/**
 * Minutes and seconds, the way the banner shows elapsed time.
 * @param seconds Length of a gap in whole seconds.
 */
function formatDuration(seconds: number): string {
	const minutes = Math.floor(seconds / 60);
	return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

type ToastView = Pick<
	StreamingState,
	'phase' | 'assemblyStatus' | 'syncStatus' | 'failure' | 'events' | 'partial' | 'silent' | 'runID'
>;

function pickView(state: StreamingState): ToastView {
	return {
		phase: state.phase,
		assemblyStatus: state.assemblyStatus,
		syncStatus: state.syncStatus,
		failure: state.failure,
		events: state.events,
		partial: state.partial,
		silent: state.silent,
		runID: state.runID,
	};
}

function useThrottledView(state: StreamingState): ToastView {
	const [view, setView] = useState(() => pickView(state));
	const latest = useRef(state);
	const lastUpdate = useRef(0);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	latest.current = state;

	useEffect(() => {
		const next = pickView(state);
		const unchanged = (Object.keys(next) as (keyof ToastView)[]).every(
			(key) => next[key] === view[key],
		);

		if (unchanged || timer.current) return;

		const wait = Math.max(0, lastUpdate.current + UPDATE_INTERVAL_MS - Date.now());

		timer.current = setTimeout(() => {
			timer.current = null;
			lastUpdate.current = Date.now();
			setView(pickView(latest.current));
		}, wait);
	}, [state, view]);

	useEffect(
		() => () => {
			if (timer.current) clearTimeout(timer.current);
		},
		[],
	);

	return view;
}

function LiveTranscriptLog({
	events,
	partial,
	silent,
	notCapturing,
}: {
	events: TranscriptEvent[];
	partial: string;
	/** Nobody has spoken for a few seconds; shown faintly in the partial line. */
	silent: boolean;
	/** Audio is being dropped while the session reconnects. */
	notCapturing: boolean;
}) {
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);
	const stickToBottom = useRef(true);

	useLayoutEffect(() => {
		const element = ref.current;
		if (element && stickToBottom.current) element.scrollTop = element.scrollHeight;
	}, [events.length]);

	return (
		<>
			{notCapturing && <Warning>{t('transcription.streaming.status.notCapturing')}</Warning>}
			<Log
				ref={ref}
				onScroll={(event) => {
					const element = event.currentTarget;
					stickToBottom.current =
						element.scrollTop + element.clientHeight >=
						element.scrollHeight - STICK_TO_BOTTOM_THRESHOLD_PX;
				}}
			>
				{events.length === 0 && <Empty>{t('transcription.streaming.logEmpty')}</Empty>}
				{events.map((event) =>
					event.kind === 'break' ? (
						<Break key={event.id}>{t('transcription.streaming.break')}</Break>
					) : (
						<Entry key={event.id}>
							{event.kind === 'transcribed' ? (
								<EntryIcon $color={tokens.success.icon}>
									<CircleCheckmarkIcon size={16} />
								</EntryIcon>
							) : (
								<EntryIcon $color={tokens.error.icon}>
									<CloseCircleIcon size={16} />
								</EntryIcon>
							)}
							<span>
								{event.kind === 'gap'
									? t('transcription.streaming.gap', {
											duration: formatDuration(event.durationSeconds ?? 0),
										})
									: event.text || t('transcription.streaming.connectionLost')}
							</span>
						</Entry>
					),
				)}
			</Log>
			<Partial>
				{partial ||
					(silent && !notCapturing && <Silence>{t('transcription.streaming.silence')}</Silence>)}
			</Partial>
		</>
	);
}

function toastTitle(view: ToastView, t: (key: string) => string): string {
	if (view.phase === 'finishing') return t('transcription.streaming.status.finishing');
	if (view.phase === 'cancelling') return t('transcription.streaming.status.cancelling');
	if (view.phase === 'countdown' || view.assemblyStatus === 'connecting') {
		return t('transcription.streaming.status.connecting');
	}
	if (view.phase === 'recording') {
		return view.assemblyStatus === 'reconnecting'
			? t('transcription.streaming.status.reconnecting')
			: t('transcription.streaming.status.transcribing');
	}
	if (view.syncStatus === 'pending' || view.syncStatus === 'syncing') {
		return t('transcription.streaming.status.saving');
	}
	if (view.failure) return t(`transcription.streaming.failure.${view.failure}`);

	return t('transcription.streaming.status.paused');
}

function toastAppearance(view: ToastView): ToastAppearance {
	if (view.failure && view.phase !== 'recording') return 'error';
	if (view.assemblyStatus === 'reconnecting' || view.syncStatus === 'failed') return 'warning';

	return 'info';
}

/**
 * One toast with the live event log of the active streaming session. It
 * stays after Pause until it is dismissed, and comes back on the next
 * Continue.
 */
export function LiveTranscriptToast() {
	const { t } = useTranslation();
	const { state } = useStreamingTranscriptionContext();
	const { addToast, updateToast, removeToast } = useToasts();
	const view = useThrottledView(state);

	const toastID = useRef<string | null>(null);
	const [dismissedRun, setDismissedRun] = useState<number | null>(null);

	const visible =
		view.runID !== dismissedRun &&
		view.phase !== 'idle' &&
		view.phase !== 'starting' &&
		view.phase !== 'interrupted';

	useEffect(() => {
		if (!visible) {
			if (toastID.current) removeToast(toastID.current);
			toastID.current = null;
			return;
		}

		const appearance = toastAppearance(view);
		const content = (
			<Toast
				appearance={appearance}
				title={toastTitle(view, t)}
				description={
					<LiveTranscriptLog
						events={view.events}
						partial={view.partial}
						silent={view.phase === 'recording' && view.silent}
						notCapturing={view.phase === 'recording' && view.assemblyStatus === 'reconnecting'}
					/>
				}
				onDismiss={() => {
					setDismissedRun(view.runID);
				}}
			/>
		);

		if (toastID.current) {
			updateToast(toastID.current, { content, appearance });
			return;
		}

		toastID.current = `live-transcript-${view.runID}`;
		addToast(content, { id: toastID.current, appearance, autoDismiss: false });
	}, [visible, view, t, addToast, updateToast, removeToast]);

	useEffect(
		() => () => {
			if (toastID.current) removeToast(toastID.current);
		},
		[removeToast],
	);

	return null;
}
